"use client";

import { useEffect, type RefObject } from "react";

import { FOCUSABLE_SELECTOR, trappedFocusIndex } from "./focus-trap";

/**
 * Prende o foco do teclado no painel enquanto `active` e devolve a quem abriu
 * ao desligar. O Radix fazia isso nas confirmações que migraram para o
 * `ReusableModal` no B3 (M-2 da revisão final).
 *
 * O Tab é escutado no próprio painel, não no `document`: popover e select
 * portados para o `body` ficam fora dele e cuidam do próprio foco.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
): void {
  useEffect(() => {
    if (!active) return;
    const container = containerRef.current;
    if (!container) return;

    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusables = () =>
      Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));

    if (!container.contains(document.activeElement)) {
      (focusables()[0] ?? container).focus();
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Tab") return;
      const items = focusables();
      const current = items.indexOf(document.activeElement as HTMLElement);
      const target = trappedFocusIndex(current, items.length, event.shiftKey);
      if (target === null) return;
      event.preventDefault();
      (target === -1 ? container : items[target])?.focus();
    };

    container.addEventListener("keydown", onKeyDown);
    return () => {
      container.removeEventListener("keydown", onKeyDown);
      if (opener?.isConnected) opener.focus();
    };
  }, [active, containerRef]);
}
